import { TextField, FormControl, Checkbox, Link, Button } from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Doctor, DoctorImageSasToken } from "../../models";
import { SelectControl } from "../Common";
import { BuildUrl } from "../../helpers";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../../constants";
import { LthCapField } from "./LocalTelehealthCapacity/LthCapField";

export type PendingDoctorImage = File | "delete" | null;

interface props {
	pendingImage: PendingDoctorImage;
	onImageChange: (image: PendingDoctorImage) => void;
	isSaving: boolean;
	doctor: Doctor | null;
	sexError?: string;
	onFieldChange: (path: string[], value: any) => void;
	onGenderChange: (value: string) => void;
}

type DoctorImageExtension = "jpg" | "png";

export const DoctorForm = ({
	doctor,
	sexError,
	onFieldChange,
	onGenderChange,
	pendingImage,
	onImageChange,
	isSaving
}: props) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [imageContainerUrl, setImageContainerUrl] = useState("");
	const [sasToken, setSasToken] = useState("");
	const [imageUploadStatus, setImageUploadStatus] = useState("");
	const [previewUrl, setPreviewUrl] = useState("");
	const imageInputRef = useRef<HTMLInputElement>(null);
	const [imageLoadFailed, setImageLoadFailed] = useState(false);
	const [imageVersion, setImageVersion] = useState(Date.now());
	const [imageExtension, setImageExtension] =
		useState<DoctorImageExtension>("jpg");

	const genderOptions = [
		{ label: "Male", value: "Male" },
		{ label: "Female", value: "Female" },
		{ label: "Other", value: "Other" }
	];

	const getDoctorImagesSasToken = useCallback(async () => {
		try {
			const url = BuildUrl("DoctorImages/sas-token");
			const tokenResult = await instance
				.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				})
				.catch(() => null);
			const headers: any = { "Content-Type": "application/json" };
			if (tokenResult) {
				headers.Authorization = `Bearer ${tokenResult.accessToken}`;
			}

			const result = await fetch(url, {
				method: "GET",
				headers
			});

			if (!result.ok) {
				throw new Error("Image upload failed");
			}
			const doctorImageSasToken: DoctorImageSasToken = await result.json();
			setImageContainerUrl(doctorImageSasToken.containerUrl);
			setSasToken(doctorImageSasToken.sasToken);
		} catch (error) {
			console.error(error);
			setImageUploadStatus("There was an error retrieving the Sas token.");
		}
	}, [account, instance]);

	useEffect(() => {
		if (doctor?.id == null) {
			return;
		}
		setImageLoadFailed(false);
		setImageExtension("jpg");
		setImageVersion(Date.now());
		getDoctorImagesSasToken();
	}, [doctor?.id, getDoctorImagesSasToken]);

	const getSasTokenQueryString = () => {
		if (!sasToken) return "";
		return sasToken.startsWith("?") ? sasToken : `?${sasToken}`;
	};

	const getDoctorImageUrl = () => {
		if (!doctor?.id || !imageContainerUrl || !sasToken) {
			return "";
		}

		return `${imageContainerUrl}/${doctor.id}.${imageExtension}${getSasTokenQueryString()}&v=${imageVersion}`;
	};

	useEffect(() => {
		if (!pendingImage || pendingImage === "delete") {
			setPreviewUrl("");
			return;
		}
		const url = URL.createObjectURL(pendingImage);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [pendingImage]);

	const handleDoctorImageLoadError = () => {
		if (imageExtension === "jpg") {
			setImageExtension("png");
			return;
		}

		setImageLoadFailed(true);
	};

	const uploadDoctorImage = (e: ChangeEvent<HTMLInputElement>) => {
		const image = e.target.files?.[0];
		if (!image) return;
		if (!doctor?.id) {
			setImageUploadStatus("Save the doctor before uploading an image.");
			e.target.value = "";
			return;
		}
		if (!["image/jpg", "image/jpeg", "image/png"].includes(image.type)) {
			setImageUploadStatus("Only JPG, JPEG, and PNG images can be uploaded.");
			e.target.value = "";
			return;
		}

		onImageChange(image);
		setImageUploadStatus("");
		e.target.value = "";
	};

	const displayedImageUrl =
		pendingImage === "delete"
			? ""
			: pendingImage
				? previewUrl
				: !imageLoadFailed
					? getDoctorImageUrl()
					: "";

	return (
		<>
			<div className="flex gap-4 mb-4">
				<TextField
					id="salutation"
					name="salutation"
					label="Salutation"
					variant="outlined"
					value={doctor?.salutation || ""}
					onChange={(e) =>
						onFieldChange(["doctor", "salutation"], e.target.value)
					}
					style={{ width: 100 }}
				/>

				<TextField
					id="firstName"
					name="firstName"
					label="First Name"
					variant="outlined"
					value={doctor?.firstName || ""}
					onChange={(e) =>
						onFieldChange(["doctor", "firstName"], e.target.value)
					}
					style={{ width: 400 }}
				/>
				<TextField
					id="lastName"
					name="lastName"
					label="Last Name"
					variant="outlined"
					value={doctor?.lastName || ""}
					onChange={(e) =>
						onFieldChange(["doctor", "lastName"], e.target.value)
					}
					style={{ width: 400 }}
				/>
			</div>

			<div className="flex flex-wrap items-start gap-4">
				<FormControl sx={{ minWidth: 120 }}>
					<SelectControl
						label="Gender"
						options={genderOptions}
						value={doctor?.sex || ""}
						required
						error={Boolean(sexError)}
						helperText={sexError}
						handleChange={onGenderChange}
					/>
				</FormControl>
				<LthCapField
					doctorId={doctor?.id}
					value={doctor?.lthDailyCap ?? null}
					onChange={(value) => onFieldChange(["doctor", "lthDailyCap"], value)}
					disabled={isSaving}
				/>

				<div className="flex items-center" style={{ height: 56 }}>
					<Checkbox
						checked={doctor?.status === true}
						onChange={(e, checked) =>
							onFieldChange(["doctor", "status"], e.target.checked)
						}
					/>
					Active
				</div>
			</div>

			<div className="mt-8">Doctor image</div>
			<div className="flex">
				{displayedImageUrl && (
					<img
						src={displayedImageUrl}
						alt={`${doctor?.displayName || "Doctor"} profile`}
						className="mb-4 h-40 w-40 object-cover mr-4"
						onError={pendingImage ? undefined : handleDoctorImageLoadError}
					/>
				)}
				<div className="flex flex-col">
					<input
						ref={imageInputRef}
						id="doctor-image"
						type="file"
						hidden
						accept="image/jpeg,image/png"
						disabled={isSaving}
						onChange={uploadDoctorImage}
					/>
					<Button
						type="button"
						variant="outlined"
						size="small"
						disabled={isSaving}
						onClick={() => imageInputRef.current?.click()}
						sx={{
							alignSelf: "flex-start",
							mb: 2,
							px: 1.25,
							py: 0.75,
							borderRadius: "24px",
							fontSize: "16px",
							textTransform: "none"
						}}
					>
						Upload image
					</Button>
					{(imageUploadStatus || pendingImage) && (
						<span className="text-sm" role="status">
							{imageUploadStatus ||
								(pendingImage === "delete"
									? "Image will be deleted when you save."
									: "Image will be uploaded when you save.")}
						</span>
					)}
					{displayedImageUrl && !isSaving && (
						<Link
							underline="none"
							onClick={() => {
								onImageChange("delete");
								setImageUploadStatus("");
							}}
						>
							Delete doctor image
						</Link>
					)}
				</div>
			</div>
		</>
	);
};
